/**
 * WHAT WE SEND THEM, AND THE OFFICE'S HOLDS, ON THE CLIENT'S CARD (ops #76 phase 2, phase 4 of
 * `docs/plan-preferences.md`, section 8; `docs/seams/home.md` 11.3).
 *
 * PER PERSON, "What we send them": their pause and their quiet hours, each with its change and its end
 * (phase 5), then a row a category offered to them, email on or off, by text too or
 * not, the reminder's lead and time, a home of theirs where a row or a hold names one, the office's
 * hold on a row, and the guard line where the visit or money contact has no way open to them. Texts
 * are read-only with their reason: they are the register's, never a row's to switch. A change asks how
 * they told the office and a reason, in one small form; an `off` that would leave the visit or money
 * contact no way to be reached is refused in the server's words until the office says it will call
 * them instead.
 *
 * ON THE HOUSEHOLD, "Hold messages": the holds that stand, each with Lift, and the form that sets one,
 * a kind or every kind, the household or one person, a home or every home, from a day until a day or
 * until lifted, and the reason. Their page at home shows each hold read-only.
 *
 * THE SERVER DECIDES EVERY ONE OF THESE AGAIN (`api/src/records/office-choices.ts`, binding rule 6):
 * nothing drawn here is the control, and every refusal is said in the server's own words.
 */
import { useState } from 'react';
import {
  OFFICE_PREFERENCE_HOW,
  OFFICE_PREFERENCE_HOW_LABELS,
  SEND_TIMES,
  SEND_TIME_LABELS,
  type OfficePreferenceHow,
  type PreferenceCategory,
  type SendTime,
} from '@fk/shared';
import {
  ApiError,
  liftHold,
  setChoice,
  setHold,
  type ChoiceSetting,
  type ContactRow,
  type MessageHold,
  type PersonChoicesView,
  type SiteRow,
} from '../api.ts';
import {
  GUARD_LINE,
  HOLDABLE_CATEGORIES,
  PAUSE_SILENCES_LINE,
  categoryLabel,
  changedWords,
  emailWords,
  heldWords,
  holdWords,
  leadWords,
  pauseWords,
  quietWords,
  settingWords,
  textsWords,
} from '../what-we-send.ts';
import { Pill } from './AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from './Field.tsx';
import { ErrorNotice } from './Notice.tsx';

/** A home's name on the card: its label, else its street. */
const homeName = (sites: readonly SiteRow[], homeId: string): string => {
  const site = sites.find((one) => one.id === homeId);
  return site ? (site.label ?? site.addressLine1) : 'a home no longer on file';
};

/** Every home, or one of them, as a select's value. */
const EVERY_HOME = 'every';

/** The office deciding a hold itself, as the how select's value. */
const THE_OFFICE = 'office';

/** The household, as the whose select's value. */
const HOUSEHOLD = 'household';

/** Every kind but the account's, as the kind select's value. */
const EVERY_KIND = 'every';

const LEADS = ['0', '1', '2', '3'] as const;

/* ------------------------------------------------------------------ *
 * Per person: what we send them.
 * ------------------------------------------------------------------ */

export function WhatWeSend({
  clientId,
  contact,
  person,
  sites,
  today,
  onDone,
}: {
  clientId: string;
  contact: ContactRow;
  person: PersonChoicesView;
  sites: readonly SiteRow[];
  today: string;
  onDone: (sentence: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ChoiceSetting | null>(null);
  const [period, setPeriod] = useState<PeriodChange | null>(null);
  const many = sites.length > 1;
  // A Worker older than phase 5 sends neither, and the card offers no change of them.
  const periods = person.paused !== undefined && person.quietHours !== undefined;
  const homeOf = (homeId: string) => homeName(sites, homeId);
  const rows = person.settings;
  const panel = `choices-${contact.id}`;

  return (
    <div className="rounded-lg border border-navy/15 p-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen((was) => !was)}
        className="flex min-h-11 w-full items-center justify-between text-left desk:min-h-9"
      >
        <span className="text-xs tracking-wide text-navy/55 uppercase">What we send them</span>
        <span className="text-sm text-navy/70">{open ? 'Close' : 'Open'}</span>
      </button>
      {open ? (
        <div id={panel} className="mt-2 flex flex-col gap-2">
          <p className="text-sm">{emailWords(person)}</p>
          <p className="text-sm">{textsWords(person)}</p>
          {periods ? (
            <div className="rounded-lg border border-navy/10 p-2" data-row="periods">
              <p className="text-sm">{pauseWords(person.paused, homeOf, today)}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                <Button tone="quiet" onClick={() => setPeriod('pause')}>
                  {person.paused ? 'Change the pause' : 'Pause messages'}
                </Button>
                {person.paused ? (
                  <Button tone="quiet" onClick={() => setPeriod('end_pause')}>
                    End the pause
                  </Button>
                ) : null}
              </div>
              <p className="mt-2 text-sm">{quietWords(person.quietHours)}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                <Button tone="quiet" onClick={() => setPeriod('quiet')}>
                  {person.quietHours ? 'Change quiet hours' : 'Set quiet hours'}
                </Button>
                {person.quietHours ? (
                  <Button tone="quiet" onClick={() => setPeriod('end_quiet')}>
                    Take off quiet hours
                  </Button>
                ) : null}
              </div>
              {period ? (
                <PeriodForm
                  key={period}
                  change={period}
                  clientId={clientId}
                  contact={contact}
                  person={person}
                  sites={sites}
                  today={today}
                  onDone={(sentence) => {
                    setPeriod(null);
                    onDone(sentence);
                  }}
                  onCancel={() => setPeriod(null)}
                />
              ) : null}
            </div>
          ) : null}
          <ul className="flex flex-col gap-2">
            {rows.map((row) => (
              <li
                key={`${row.category}:${row.homeId ?? EVERY_HOME}`}
                className="rounded-lg border border-navy/10 p-2"
                data-row={`${row.category}:${row.homeId ?? EVERY_HOME}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{categoryLabel(row.category)}</span>
                  {many || row.homeId !== null ? (
                    <Pill>{row.homeId === null ? 'Every home' : homeName(sites, row.homeId)}</Pill>
                  ) : null}
                  {row.heldByOffice ? (
                    <Pill tone="gold">{heldWords(row.heldByOffice, today)}</Pill>
                  ) : null}
                </div>
                <p className="mt-1 text-sm">{settingWords(row)}</p>
                {row.category === 'account' ? null : (
                  <p className="mt-0.5 text-xs text-navy/65">{changedWords(row)}</p>
                )}
                {row.officeCalls ? (
                  <p className="mt-1 text-sm text-coral" role="note">
                    {GUARD_LINE}
                  </p>
                ) : null}
                {row.category === 'account' ? null : (
                  <div className="mt-1">
                    <Button tone="quiet" onClick={() => setEditing(row)}>
                      Change
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {editing && editing.category !== 'account' ? (
            <ChoiceForm
              key={`${editing.category}:${editing.homeId ?? EVERY_HOME}`}
              clientId={clientId}
              contact={contact}
              person={person}
              row={editing as ChoiceSetting & { category: PreferenceCategory }}
              sites={sites}
              onDone={(sentence) => {
                setEditing(null);
                onDone(sentence);
              }}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * ONE CHOICE CHANGED ON THEIR WORD: the home, the email on or off, the reminder's lead and time, how
 * they told the office and the reason. A refusal is shown in the server's words; the lock's offers the
 * office the call instead, which is sent only on that second press.
 */
function ChoiceForm({
  clientId,
  contact,
  person,
  row,
  sites,
  onDone,
  onCancel,
}: {
  clientId: string;
  contact: ContactRow;
  person: PersonChoicesView;
  row: ChoiceSetting & { category: PreferenceCategory };
  sites: readonly SiteRow[];
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `choice-${contact.id}-${name}`;
  const homes = person.homesOffered;
  const [home, setHome] = useState<string>(row.homeId ?? EVERY_HOME);
  const [email, setEmail] = useState<'on' | 'off'>(row.email === 'on' ? 'off' : 'on');
  const [lead, setLead] = useState<(typeof LEADS)[number]>(
    String(row.leadDays ?? 2) as (typeof LEADS)[number],
  );
  const [time, setTime] = useState<SendTime>(row.sendTime ?? 'morning');
  const [how, setHow] = useState<OfficePreferenceHow>('call');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [locked, setLocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reminder = row.category === 'visit_reminders';
  const label = categoryLabel(row.category);

  async function save(officeCalls: boolean) {
    if (reason.trim() === '') {
      setProblem('Say why, in a few words: what they asked for.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const answer = await setChoice(clientId, contact.id, {
        kind: 'choice',
        category: row.category,
        homeId: home === EVERY_HOME ? null : home,
        email,
        ...(reminder ? { leadDays: Number(lead), sendTime: time } : {}),
        how,
        reason: reason.trim(),
        ...(officeCalls ? { officeCalls: true } : {}),
      });
      onDone(
        [
          `Saved. Email for ${label.toLowerCase()} is ${email} for ${contact.displayName} now, and the history keeps what it said before.`,
          answer.officeCalls ? GUARD_LINE : null,
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.code === 'locked') {
        setLocked(thrown.message);
      } else {
        setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-navy/20 p-3">
      <p className="mb-3 text-sm font-medium">
        {label}, for {contact.displayName}
      </p>
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not saved" message={problem} />
        </div>
      ) : null}
      {homes.length > 1 ? (
        <SelectField
          id={field('home')}
          label="Which home"
          value={home}
          onChange={setHome}
          options={[EVERY_HOME, ...homes]}
          toLabel={(value) => (value === EVERY_HOME ? 'Every home' : homeName(sites, value))}
        />
      ) : null}
      <SelectField
        id={field('email')}
        label="By email"
        value={email}
        onChange={setEmail}
        options={['on', 'off'] as const}
        toLabel={(value) => (value === 'on' ? 'Email on' : 'Email off')}
      />
      {reminder ? (
        <>
          <SelectField
            id={field('lead')}
            label="How far ahead"
            value={lead}
            onChange={setLead}
            options={LEADS}
            toLabel={(value) => leadWords(Number(value))}
          />
          <SelectField
            id={field('time')}
            label="What time of day"
            value={time}
            onChange={setTime}
            options={SEND_TIMES}
            toLabel={(value) => SEND_TIME_LABELS[value]}
          />
        </>
      ) : null}
      <SelectField
        id={field('how')}
        label="How they told the office"
        value={how}
        onChange={setHow}
        options={OFFICE_PREFERENCE_HOW}
        toLabel={(value) => OFFICE_PREFERENCE_HOW_LABELS[value]}
      />
      <TextAreaField
        id={field('reason')}
        label="Why"
        value={reason}
        onChange={setReason}
        rows={2}
        hint="A short reason, 200 characters at most. The office's own: their page at home never shows it."
      />
      {locked ? (
        <div className="mb-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
          <p className="text-sm">{locked}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button onClick={() => void save(true)} disabled={busy}>
              {busy ? 'Saving' : 'They asked for it: the office will call them'}
            </Button>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {locked ? null : (
          <Button onClick={() => void save(false)} disabled={busy}>
            {busy ? 'Saving' : 'Save the choice'}
          </Button>
        )}
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Leave it as it is
        </Button>
      </div>
    </div>
  );
}

/** Which of their pause and quiet hours the office is changing, or ending. */
type PeriodChange = 'pause' | 'end_pause' | 'quiet' | 'end_quiet';

/** The hours a quiet window may run between, on the half hour, inside the texting hours. */
const HALF_HOURS: readonly string[] = Array.from(
  { length: 25 },
  (_, n) => `${String(8 + Math.floor(n / 2)).padStart(2, '0')}:${n % 2 === 0 ? '00' : '30'}`,
);

/**
 * THEIR PAUSE OR THEIR QUIET HOURS ON THEIR WORD (phase 5): a pause's home, first day and last, or the
 * window's start and end; or the end of either. How they told the office and the reason with each, and
 * a refusal in the server's words.
 */
function PeriodForm({
  change,
  clientId,
  contact,
  person,
  sites,
  today,
  onDone,
  onCancel,
}: {
  change: PeriodChange;
  clientId: string;
  contact: ContactRow;
  person: PersonChoicesView;
  sites: readonly SiteRow[];
  today: string;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `period-${contact.id}-${name}`;
  const homes = person.homesOffered;
  const [home, setHome] = useState<string>(person.paused?.homeId ?? EVERY_HOME);
  const [from, setFrom] = useState(person.paused?.from ?? today);
  const [until, setUntil] = useState(person.paused?.until ?? '');
  const [quietFrom, setQuietFrom] = useState(person.quietHours?.from ?? '09:00');
  const [quietUntil, setQuietUntil] = useState(person.quietHours?.until ?? '17:00');
  const [how, setHow] = useState<OfficePreferenceHow>('call');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const title = {
    pause: `Pause messages for ${contact.displayName}`,
    end_pause: `End the pause for ${contact.displayName}`,
    quiet: `Quiet hours for ${contact.displayName}`,
    end_quiet: `Take off quiet hours for ${contact.displayName}`,
  }[change];

  async function save() {
    if (reason.trim() === '') {
      setProblem('Say why, in a few words: what they asked for.');
      return;
    }
    if (change === 'pause' && from < today) {
      setProblem('A pause starts today or on a later day.');
      return;
    }
    if (change === 'pause' && until !== '' && until < from) {
      setProblem(
        'It ends on or after the day it starts, or leave the end empty until it is ended.',
      );
      return;
    }
    if (change === 'quiet' && quietFrom >= quietUntil) {
      setProblem('The window ends after it starts.');
      return;
    }
    setBusy(true);
    setProblem(null);
    const said = { how, reason: reason.trim() };
    try {
      if (change === 'pause' || change === 'end_pause') {
        await setChoice(clientId, contact.id, {
          kind: 'pause',
          pause:
            change === 'end_pause'
              ? null
              : {
                  from,
                  until: until === '' ? null : until,
                  homeId: home === EVERY_HOME ? null : home,
                },
          ...said,
        });
        onDone(
          change === 'end_pause'
            ? `Ended. What the pause held goes again from today for ${contact.displayName}, and the history keeps it.`
            : `Paused for ${contact.displayName}. ${PAUSE_SILENCES_LINE}`,
        );
      } else {
        await setChoice(clientId, contact.id, {
          kind: 'quiet',
          quietHours: change === 'end_quiet' ? null : { from: quietFrom, until: quietUntil },
          ...said,
        });
        onDone(
          change === 'end_quiet'
            ? `Taken off. A text to ${contact.displayName} goes whenever the texting hours allow.`
            : `Saved. A text to ${contact.displayName} waits for ${quietFrom} to ${quietUntil}, and one that cannot reach them in time is not sent.`,
        );
      }
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-navy/20 p-3">
      <p className="mb-3 text-sm font-medium">{title}</p>
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not saved" message={problem} />
        </div>
      ) : null}
      {change === 'pause' ? (
        <>
          {homes.length > 1 ? (
            <SelectField
              id={field('home')}
              label="Which home"
              value={home}
              onChange={setHome}
              options={[EVERY_HOME, ...homes]}
              toLabel={(value) => (value === EVERY_HOME ? 'Every home' : homeName(sites, value))}
            />
          ) : null}
          <TextField id={field('from')} label="From" type="date" value={from} onChange={setFrom} />
          <TextField
            id={field('until')}
            label="Until"
            type="date"
            value={until}
            onChange={setUntil}
            hint="Empty pauses until somebody ends it. One pause a person: this replaces any other."
          />
        </>
      ) : null}
      {change === 'quiet' ? (
        <>
          <SelectField
            id={field('quiet-from')}
            label="Texts from"
            value={quietFrom}
            onChange={setQuietFrom}
            options={HALF_HOURS.slice(0, -1)}
            toLabel={(value) => value}
          />
          <SelectField
            id={field('quiet-until')}
            label="Texts until"
            value={quietUntil}
            onChange={setQuietUntil}
            options={HALF_HOURS.slice(1)}
            toLabel={(value) => value}
          />
        </>
      ) : null}
      <SelectField
        id={field('how')}
        label="How they told the office"
        value={how}
        onChange={setHow}
        options={OFFICE_PREFERENCE_HOW}
        toLabel={(value) => OFFICE_PREFERENCE_HOW_LABELS[value]}
      />
      <TextAreaField
        id={field('reason')}
        label="Why"
        value={reason}
        onChange={setReason}
        rows={2}
        hint="A short reason, 200 characters at most. The office's own: their page at home never shows it."
      />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving' : change.startsWith('end') ? 'End it' : 'Save'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Leave it as it is
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * On the household: the office's holds.
 * ------------------------------------------------------------------ */

export function HoldMessages({
  clientId,
  contacts,
  sites,
  holds,
  today,
  onDone,
}: {
  clientId: string;
  contacts: readonly ContactRow[];
  sites: readonly SiteRow[];
  holds: readonly MessageHold[];
  today: string;
  onDone: (sentence: string) => void;
}) {
  const [setting, setSetting] = useState(false);
  const [lifting, setLifting] = useState<MessageHold | null>(null);
  const nameOf = (personId: string) =>
    contacts.find((one) => one.id === personId)?.displayName ?? 'a person no longer on file';
  const homeOf = (homeId: string) => homeName(sites, homeId);

  return (
    <div className="rounded-lg border border-navy/15 p-3" id="hold-messages">
      <p className="text-xs tracking-wide text-navy/55 uppercase">Hold messages</p>
      {holds.length === 0 ? (
        <p className="mt-1 text-sm text-navy/70">The office holds nothing for this household.</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {holds.map((hold) => (
            <li key={hold.id} className="rounded-lg border border-navy/10 p-2" data-hold={hold.id}>
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={hold.covering ? 'gold' : 'quiet'}>
                  {hold.covering ? 'Holding now' : 'Starts later'}
                </Pill>
              </div>
              <p className="mt-1 text-sm">{holdWords(hold, nameOf, homeOf)}</p>
              {hold.note ? (
                <p className="mt-0.5 text-xs text-navy/65">
                  {hold.changedByName ? `${hold.changedByName}: ` : ''}
                  {hold.note}
                </p>
              ) : null}
              {lifting?.id === hold.id ? (
                <LiftForm
                  clientId={clientId}
                  hold={hold}
                  onDone={(sentence) => {
                    setLifting(null);
                    onDone(sentence);
                  }}
                  onCancel={() => setLifting(null)}
                />
              ) : (
                <div className="mt-1">
                  <Button tone="quiet" onClick={() => setLifting(hold)}>
                    Lift
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {setting ? (
        <HoldForm
          clientId={clientId}
          contacts={contacts}
          sites={sites}
          today={today}
          onDone={(sentence) => {
            setSetting(false);
            onDone(sentence);
          }}
          onCancel={() => setSetting(false)}
        />
      ) : (
        <div className="mt-2">
          <Button tone="quiet" onClick={() => setSetting(true)}>
            Hold messages
          </Button>
        </div>
      )}
    </div>
  );
}

/** How a person asked, or the office deciding it itself, as a hold's and a lift's select offers. */
const HOLD_HOWS = [THE_OFFICE, ...OFFICE_PREFERENCE_HOW] as const;
type HoldHow = (typeof HOLD_HOWS)[number];
const holdHowLabel = (value: HoldHow): string =>
  value === THE_OFFICE
    ? 'The office decided it'
    : `They asked, ${OFFICE_PREFERENCE_HOW_LABELS[value].toLowerCase()}`;
const howOf = (value: HoldHow): OfficePreferenceHow | null => (value === THE_OFFICE ? null : value);

/** A hold set: what, whose, where, from when until when, and why. */
function HoldForm({
  clientId,
  contacts,
  sites,
  today,
  onDone,
  onCancel,
}: {
  clientId: string;
  contacts: readonly ContactRow[];
  sites: readonly SiteRow[];
  today: string;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `hold-${clientId}-${name}`;
  const [kind, setKind] = useState<string>(EVERY_KIND);
  const [whose, setWhose] = useState<string>(HOUSEHOLD);
  const [home, setHome] = useState<string>(EVERY_HOME);
  const [from, setFrom] = useState(today);
  const [until, setUntil] = useState('');
  const [how, setHow] = useState<HoldHow>(THE_OFFICE);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function hold() {
    if (reason.trim() === '') {
      setProblem('Say why, in a few words.');
      return;
    }
    if (from < today) {
      setProblem('A hold starts today or on a later day.');
      return;
    }
    if (until !== '' && until < from) {
      setProblem(
        'It ends on or after the day it starts, or leave the end empty to hold until lifted.',
      );
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const answer = await setHold(clientId, {
        personId: whose === HOUSEHOLD ? null : whose,
        homeId: home === EVERY_HOME ? null : home,
        category: kind === EVERY_KIND ? null : (kind as PreferenceCategory),
        from,
        until: until === '' ? null : until,
        how: howOf(how),
        reason: reason.trim(),
      });
      onDone(
        answer.officeCalls
          ? "Held. Their page at home shows the hold. Nothing it holds is sent, and the office is given the call for each change to a visit or invoice it holds for the household's contact for it."
          : 'Held. Their page at home shows the hold. Nothing it holds is sent, and no call is raised for it: tell them another way if they need to know.',
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/20 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not held" message={problem} />
        </div>
      ) : null}
      <SelectField
        id={field('kind')}
        label="What to hold"
        value={kind}
        onChange={setKind}
        options={[EVERY_KIND, ...HOLDABLE_CATEGORIES]}
        toLabel={(value) =>
          value === EVERY_KIND
            ? "Every message but the account's"
            : categoryLabel(value as PreferenceCategory)
        }
      />
      <SelectField
        id={field('whose')}
        label="For whom"
        value={whose}
        onChange={setWhose}
        options={[HOUSEHOLD, ...contacts.map((one) => one.id)]}
        toLabel={(value) =>
          value === HOUSEHOLD
            ? 'Everybody on the household'
            : (contacts.find((one) => one.id === value)?.displayName ?? value)
        }
      />
      {sites.length > 1 ? (
        <SelectField
          id={field('home')}
          label="Which home"
          value={home}
          onChange={setHome}
          options={[EVERY_HOME, ...sites.map((one) => one.id)]}
          toLabel={(value) => (value === EVERY_HOME ? 'Every home' : homeName(sites, value))}
        />
      ) : null}
      <TextField id={field('from')} label="From" type="date" value={from} onChange={setFrom} />
      <TextField
        id={field('until')}
        label="Until"
        type="date"
        value={until}
        onChange={setUntil}
        hint="Empty holds until somebody lifts it."
      />
      <SelectField
        id={field('how')}
        label="Who asked"
        value={how}
        onChange={setHow}
        options={HOLD_HOWS}
        toLabel={holdHowLabel}
      />
      <TextAreaField
        id={field('reason')}
        label="Why"
        value={reason}
        onChange={setReason}
        rows={2}
        hint="A short reason, 200 characters at most. Their page at home shows the hold, never the reason."
      />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void hold()} disabled={busy}>
          {busy ? 'Holding' : 'Hold them'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/** A hold lifted: who asked and why, then the press. */
function LiftForm({
  clientId,
  hold,
  onDone,
  onCancel,
}: {
  clientId: string;
  hold: MessageHold;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `lift-${hold.id}-${name}`;
  const [how, setHow] = useState<HoldHow>(THE_OFFICE);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function lift() {
    if (reason.trim() === '') {
      setProblem('Say why, in a few words.');
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await liftHold(clientId, hold.id, { how: howOf(how), reason: reason.trim() });
      onDone('Lifted. What it held goes again from today, and the history keeps the hold.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-navy/20 p-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not lifted" message={problem} />
        </div>
      ) : null}
      <SelectField
        id={field('how')}
        label="Who asked"
        value={how}
        onChange={setHow}
        options={HOLD_HOWS}
        toLabel={holdHowLabel}
      />
      <TextAreaField
        id={field('reason')}
        label="Why"
        value={reason}
        onChange={setReason}
        rows={2}
      />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void lift()} disabled={busy}>
          {busy ? 'Lifting' : 'Lift the hold'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}
